import {
  useContext,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import Download from "@mui/icons-material/Download";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedThalamosUser,
  Section12PaymentRequestStatus,
  Section12PaymentRequestWithDetails,
} from "@thalamos/aspire-common";
import {
  canDoctorEditPaymentRequest,
  canDoctorInitiateS12Payments,
  DOCTOR_DELETABLE_PAYMENT_STATUSES,
  hasDoctorGmcNumber,
  isIndependentUserSession,
  isUnconfirmedEstimatedPaymentRequest,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { LoggedInUserContext } from "../../Contexts.js";
import { routeFns } from "../../routePaths.js";
import {
  assertPaymentRequestWriteAccepted,
  S12PaymentRequestDialog,
} from "../FormProgressPage/admission/S12PaymentRequestDialog.js";
import {
  buildCsvFileName,
  downloadCsvBlob,
  formatCurrency,
  formatPaymentDate,
  STATUS_CHIP_SX,
  STATUS_COLORS,
  STATUS_LABELS,
  UNKNOWN_LOCAL_AUTHORITY_LABEL,
} from "./payment-request-helpers.js";
import { PaymentRequestDetailDialog } from "./PaymentRequestDetailDialog.js";
import { useEditPaymentRequest } from "./useEditPaymentRequest.js";
import {
  LoadingSpinner,
  MenuFlyout,
  renderErrorToast,
  renderSuccessToast,
  type MenuOptionsType,
} from "~/components/design-system/index.js";

export function MyPaymentRequestsPage() {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<
    Section12PaymentRequestStatus | ""
  >("");
  const [fromDate, setFromDate] = useState<string>(
    dayjs().subtract(90, "days").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [selectedRequest, setSelectedRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);
  const {
    editingRequest,
    startEdit: handleStartEdit,
    stopEdit: clearEditingRequest,
  } = useEditPaymentRequest();
  const [deletingRequest, setDeletingRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [{ data, loading }, refetchMyPaymentRequests] =
    apiHooks.section12.getMyPaymentRequests({
      status: statusFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });

  const canAccessPaymentRequests =
    hasDoctorGmcNumber(user) &&
    (isIndependentUserSession(user) ||
      (!!user?.sessionOrganisationConfiguration &&
        canDoctorInitiateS12Payments(user.sessionOrganisationConfiguration)));

  if (!canAccessPaymentRequests) {
    return <Navigate to={routeFns.home()} replace />;
  }

  const handleExportCsv = async () => {
    try {
      const response = await api.section12.exportMyPaymentRequestsCsv({
        status: statusFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const fileName = buildCsvFileName(
        "my-payment-requests",
        fromDate || undefined,
        toDate || undefined,
      );
      downloadCsvBlob(blob, fileName);
      renderSuccessToast({ message: "CSV downloaded successfully" });
    } catch (error) {
      console.error("Failed to export CSV:", error);
      renderErrorToast({ message: "Failed to export CSV" });
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <LoadingSpinner label="Loading payment requests..." />
      </Box>
    );
  }

  const paymentRequests = data?.requests ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 0,
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            My Payment Requests
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            View all your payment requests across assessments.
          </Typography>
        </Box>
      </Box>
      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(
                e.target.value as Section12PaymentRequestStatus | "",
              );
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending AMHP approval</MenuItem>
            <MenuItem value="accepted">Approved by AMHP</MenuItem>
            <MenuItem value="rejected">Rejected by AMHP</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="payment-processing">Payment Processing</MenuItem>
            <MenuItem value="payment-on-hold">Payment on hold</MenuItem>
            <MenuItem value="receipt-missing">Receipt Missing</MenuItem>
            <MenuItem value="bank-information-missing">
              Bank Information Missing
            </MenuItem>
            <MenuItem value="value-incorrect">Value Incorrect</MenuItem>
            <MenuItem value="registration-with-icb-needed">
              Registration with ICB needed
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="From Date"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPage(0);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <TextField
          label="To Date"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPage(0);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => {
            void handleExportCsv();
          }}
          size="small"
        >
          Export CSV
        </Button>
      </Box>
      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>AMHP Team</TableCell>
              <TableCell>Local Authority</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No payment requests found
                </TableCell>
              </TableRow>
            ) : (
              paymentRequests.map(
                (request: Section12PaymentRequestWithDetails) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {request.paymentReference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {request.amhpTeam?.name ?? "Unknown Team"}
                    </TableCell>
                    <TableCell>
                      {request.localAuthority?.name ??
                        UNKNOWN_LOCAL_AUTHORITY_LABEL}
                    </TableCell>
                    <TableCell align="right">
                      {isUnconfirmedEstimatedPaymentRequest(request)
                        ? "Estimated"
                        : formatCurrency(request.totalAmountPence)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[request.status]}
                        color={STATUS_COLORS[request.status]}
                        sx={STATUS_CHIP_SX[request.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatPaymentDate(request.created)}</TableCell>
                    <TableCell>
                      <PaymentRequestRowActions
                        request={request}
                        onViewDetails={() => {
                          setSelectedRequest(request);
                        }}
                        onEdit={() => {
                          void handleStartEdit(request);
                        }}
                        onDelete={() => {
                          setDeletingRequest(request);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ),
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <PaymentRequestDetailDialog
        request={selectedRequest}
        onClose={() => {
          setSelectedRequest(null);
        }}
        onEdit={(request) => {
          setSelectedRequest(null);
          void handleStartEdit(request);
        }}
        onDelete={(request) => {
          setSelectedRequest(null);
          setDeletingRequest(request);
        }}
      />
      {editingRequest && user && (
        <EditPaymentRequestFlow
          editingRequest={editingRequest}
          user={user}
          onClose={clearEditingRequest}
          onSaved={() => {
            clearEditingRequest();
            void refetchMyPaymentRequests();
          }}
        />
      )}
      <Dialog
        open={deletingRequest !== null}
        onClose={() => {
          if (!isDeleting) setDeletingRequest(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete payment request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the request from your records. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeletingRequest(null);
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={() => {
              void handleConfirmDelete();
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  async function handleConfirmDelete() {
    if (!deletingRequest) return;
    setIsDeleting(true);
    try {
      assertPaymentRequestWriteAccepted(
        await api.section12.deletePaymentRequest(deletingRequest.id),
      );
      renderSuccessToast({ message: "Payment request deleted" });
      setDeletingRequest(null);
      void refetchMyPaymentRequests();
    } catch (error) {
      console.error("Failed to delete payment request:", error);
      renderErrorToast({ message: "Failed to delete payment request" });
    } finally {
      setIsDeleting(false);
    }
  }
}

interface PaymentRequestRowActionsProps {
  request: Section12PaymentRequestWithDetails;
  onViewDetails: () => void;
  onEdit: () => Promisable<void>;
  onDelete: () => Promisable<void>;
}

function PaymentRequestRowActions({
  request,
  onViewDetails,
  onEdit,
  onDelete,
}: PaymentRequestRowActionsProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Guest-backed requests can't be fully edited/reassigned (the update path
  // requires an "amhp" team); limited edits still work. See
  // canDoctorEditPaymentRequest for the shared rule.
  const canEdit = canDoctorEditPaymentRequest(request);
  const canDelete = (
    DOCTOR_DELETABLE_PAYMENT_STATUSES as readonly string[]
  ).includes(request.status);

  const options: MenuOptionsType[] = [
    {
      icon: "view",
      name: "View Details",
      disabled: false,
      onClick: onViewDetails,
    },
    ...(canEdit
      ? [{ icon: "edit", name: "Edit", disabled: false, onClick: onEdit }]
      : []),
    ...(canDelete
      ? [{ icon: "delete", name: "Delete", disabled: false, onClick: onDelete }]
      : []),
  ];

  return (
    <Box
      ref={cardRef}
      // Toggle from the render-time value (not a functional updater): picking a
      // menu item fires MenuFlyout's onClose (setIsMenuOpen(false)) and the
      // click then bubbles here in the same React event. The snapshot value
      // (true) resolves to false and closes; a functional updater would see the
      // queued false and flip it back to true, reopening the menu.
      onClick={(e) => {
        e.stopPropagation();
        setIsMenuOpen(!isMenuOpen);
      }}
    >
      <MenuFlyout
        cardRef={cardRef}
        options={options}
        isOpen={isMenuOpen}
        onClose={() => {
          setIsMenuOpen(false);
        }}
      />
    </Box>
  );
}

interface EditPaymentRequestFlowProps {
  editingRequest: Section12PaymentRequestWithDetails;
  user: ExtendedThalamosUser;
  onClose: () => void;
  onSaved: () => void;
}

function EditPaymentRequestFlow({
  editingRequest,
  user,
  onClose,
  onSaved,
}: EditPaymentRequestFlowProps) {
  const [{ data: formContext, loading, error }] = apiHooks.forms.getFormContext(
    editingRequest.formContextId,
  );

  // Side effects must run on commit, not during render — firing the toast
  // and onClose inline triggers a setState-during-render warning and
  // re-fires on every render until the parent unmounts.
  const hasLoadFailed = !!error || (!loading && !formContext);
  useEffect(() => {
    if (hasLoadFailed) {
      renderErrorToast({
        message: "Unable to load this payment request for editing",
      });
      onClose();
    }
  }, [hasLoadFailed, onClose]);

  if (loading && !formContext) {
    return null;
  }

  if (hasLoadFailed || !formContext) {
    return null;
  }

  return (
    <S12PaymentRequestDialog
      key={`${editingRequest.id}:${editingRequest.updated}`}
      formContext={formContext}
      user={user}
      editingRequest={editingRequest}
      onClose={onClose}
      reloadFormContext={onSaved}
    />
  );
}
